import { Link, useLocation } from 'react-router-dom';
import { Fragment, useMemo } from 'react';
import { navFlat } from '@/config/navigation';
import { ChevronRightIcon, HomeIcon } from '@/icons';

/** Path prefixes with no page of their own: rendered as plain text, not links. */
const NON_LINKABLE = new Set(['/invitations', '/organizations']);

/** Human label for a path segment: nav label if known, else title-cased. */
function labelForSegment(segment: string, fullPath: string): string {
  const match = navFlat.find((n) => n.to === fullPath || n.to.endsWith(`/${segment}`));
  if (match) return match.label;
  // Dynamic ids stay as-is (truncated); words get title-cased.
  if (/^[0-9a-f-]{8,}$/i.test(segment)) return segment.slice(0, 8);
  // Opaque tokens (e.g. invitation links) are never echoed into the UI.
  if (segment.startsWith('nhi_') || segment.length > 32) return 'Details';
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
}

/**
 * Route-derived breadcrumbs. Builds a cumulative path per segment so each crumb
 * is a real link; the last crumb is the current page (not linked).
 */
export function Breadcrumbs() {
  const { pathname } = useLocation();

  const crumbs = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    let acc = '';
    return segments.map((seg) => {
      acc += `/${seg}`;
      return { label: labelForSegment(seg, acc), to: acc };
    });
  }, [pathname]);

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1 text-sm text-fg-muted">
        <li>
          <Link to="/" className="flex items-center gap-1 hover:text-fg" aria-label="Home">
            <HomeIcon className="h-4 w-4" />
          </Link>
        </li>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <Fragment key={crumb.to}>
              <li aria-hidden="true"><ChevronRightIcon className="h-4 w-4 text-fg-subtle" /></li>
              <li>
                {isLast || NON_LINKABLE.has(crumb.to) ? (
                  <span className={isLast ? 'font-medium text-fg' : undefined} aria-current={isLast ? 'page' : undefined}>{crumb.label}</span>
                ) : (
                  <Link to={crumb.to} className="hover:text-fg">{crumb.label}</Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
